import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonIcon,
  DateAndTimePicker,
  FooterActionBar,
  Icon,
  InlineMessage,
  Link as DSLink,
  ModalIsland,
  RadioButton,
  StackingAlertBanner,
  Typography,
} from '@saviynt/design-system';
import { addMonths, subYears } from 'date-fns';

import { useInvertedLocalize, useTypedSelector } from '../../../shared/hooks';
import { type RootState } from '../../../shared/types';
import { useCreateAuditLogExportMutation } from '../data-layer/auditLogApi';
import { DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS } from '../data-layer/auditLogUtils';
import { useDateFormat } from '../hooks';
import {
  extractApiErrorMessage,
  getDateAndTimePickerFormatForGlobalDateFormat,
  toUtcIso,
} from '../utils/utils';

// Reuse the established Report modal styles for visual consistency
import '../../../features/ViolationsReport/components/ViolationsReport.css';
import './AuditLogReportModal.css';

interface AuditLogReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  requestAccessKey?: number | string;
}

/**
 * AuditLogReportModal
 *
 * Modal with the standard Report layout (header, tabs, body, footer)
 * used from the Audit Log Viewer header export action.
 *
 * @param {AuditLogReportModalProps} props - controls modal visibility and close handler
 * @returns React element rendering the report modal UI
 */
function AuditLogReportModal({
  isOpen,
  onClose,
  requestAccessKey,
}: AuditLogReportModalProps) {
  const [createExport, { isLoading }] = useCreateAuditLogExportMutation();
  const connectionType = useTypedSelector((s: RootState) => {
    const v = (
      s.aag.auditLog as { requestMetadata?: { connectiontype?: unknown } }
    ).requestMetadata?.connectiontype;

    return typeof v === 'string' ? v : undefined;
  });
  const exportMaxDateRangeMonths = useTypedSelector(
    (state: RootState): number => {
      const raw: unknown = state.aag.auditLog.exportMaxDateRangeMonths;

      if (
        typeof raw === 'number' &&
        Number.isFinite(raw) &&
        Number.isInteger(raw) &&
        raw > 0
      ) {
        return Number(raw);
      }

      return Number(DEFAULT_AUDIT_EXPORT_MAX_DATE_RANGE_MONTHS);
    }
  );

  const appLocale: string = useTypedSelector((state: RootState) => {
    const raw = state.user.userInfo?.userSavRoleMap?.locale;

    if (typeof raw === 'string') {
      const t = raw.trim();

      return t.length > 0 ? t : 'en';
    }

    return 'en';
  });
  const globalDateFormat = useDateFormat();

  const l = useInvertedLocalize();

  const [radioValue, setRadioValue] = useState<'summary' | 'detail'>('summary');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(new Date());
  const rangeMaxDate = new Date();
  const rangeMinDate = subYears(new Date(), 20);

  const [bannerState, setBannerState] = useState({
    isVisible: false,
    title: l('Exporting'),
    description: '',
    colorTheme: 'Information',
    autoDismissDelay: 8000,
    linkComponent: null as React.ReactNode | null,
  });

  const handleClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    value: string
  ) => {
    setRadioValue(value === 'detail' ? 'detail' : 'summary');
  };

  const effectiveDateFormat = useMemo(
    () => getDateAndTimePickerFormatForGlobalDateFormat(globalDateFormat),
    [globalDateFormat]
  );

  const isDetailInvalid = useMemo(() => {
    if (radioValue !== 'detail') {
      return false;
    }

    if (!(startDate instanceof Date) || !(endDate instanceof Date)) {
      return true;
    }

    const maxEnd = addMonths(startDate, exportMaxDateRangeMonths);

    return (
      endDate.getTime() < startDate.getTime() ||
      endDate.getTime() > maxEnd.getTime()
    );
  }, [radioValue, startDate, endDate, exportMaxDateRangeMonths]);

  const detailDateRangeHelpText = useMemo(
    () =>
      l(
        'End date must be on/after start date and within {months} months of start date',
        { months: exportMaxDateRangeMonths }
      ),
    [exportMaxDateRangeMonths, l]
  );

  const header = (
    <Box tag='header' className='ReportModal-headerWrapper'>
      <Box className='ReportModal-header'>
        <Icon kind='Export' />
        <Box className='ModalIsland-titleContainer'>
          <Typography kind='h2' className='ModalIsland-title'>
            {l('Export Activity Log')}
          </Typography>
        </Box>
      </Box>
      <Box tag='div' className='ModalIsland-closeBtn'>
        <ButtonIcon
          id='modalCloseButton-auditLogReportModal'
          dataTestId='ModalIslandCloseButton'
          size='medium'
          kind='ghost'
          ariaLabel={l('close')}
          icon={<Icon kind='Close' color='neutral-100' size='smallMedium' />}
          onClick={onClose}
        />
      </Box>
    </Box>
  );

  const headerComp = <Box className='ReportModal-headerComp'>{header}</Box>;

  const body = (
    <Box className='ReportModal-body'>
      {bannerState.isVisible ? (
        <StackingAlertBanner
          className='AuditLogReportModal-alertBanner'
          kind='high'
          colorTheme={bannerState.colorTheme}
          title={bannerState.title}
          description={bannerState.description}
          isVisible
          shouldAutoDismiss
          autoDismissDelay={bannerState.autoDismissDelay}
          LinkComponent={bannerState.linkComponent ?? undefined}
          onCancel={() => setBannerState((s) => ({ ...s, isVisible: false }))}
        />
      ) : null}
      <Box className='ReportModal-templates'>
        <Box>
          {(() => {
            const templateOptions: {
              id: string;
              value: 'summary' | 'detail';
              titleKey: string;
              descKey: string;
            }[] = [
              {
                id: 'radio-summary',
                value: 'summary',
                titleKey: 'Current Request',
                descKey: 'Export all activity logs related to this request.',
              },
              {
                id: 'radio-detail',
                value: 'detail',
                titleKey: 'Historical Data',
                descKey:
                  'Export activity logs from a specific time period, including older records that may not appear in the current request view.',
              },
            ];

            return (
              <Box className='AuditLogReportModal-optionsGrid'>
                {templateOptions.map((opt) => (
                  <RadioButton
                    key={opt.id}
                    id={opt.id}
                    name='radioGroupKinds'
                    title={l(opt.titleKey)}
                    description={l(opt.descKey)}
                    value={opt.value}
                    radioSelection={radioValue}
                    onClick={handleClick}
                  />
                ))}
                {radioValue === 'detail' ? (
                  <Box className='AuditLogReportModal-dateAndTimePicker'>
                    <DateAndTimePicker
                      kind='dateAndTimeStart'
                      label={l('Start Date')}
                      startDate={startDate}
                      setStartDate={setStartDate}
                      endDate={endDate}
                      maxDate={rangeMaxDate}
                      minDate={rangeMinDate}
                      dateFormat={effectiveDateFormat}
                    />
                    <DateAndTimePicker
                      kind='dateAndTimeEnd'
                      label={l('End Date')}
                      startDate={startDate}
                      setStartDate={setStartDate}
                      endDate={endDate}
                      setEndDate={setEndDate}
                      maxDate={rangeMaxDate}
                      minDate={rangeMinDate}
                      dateFormat={effectiveDateFormat}
                      isCritical={isDetailInvalid}
                      CriticalHelperText={
                        <InlineMessage
                          text={detailDateRangeHelpText}
                          colorTheme='critical'
                          size='small'
                          leftIcon={
                            <Icon
                              kind='AlertCriticalFilled'
                              color='--color-foreground-critical-bold'
                            />
                          }
                        />
                      }
                    />
                  </Box>
                ) : null}
              </Box>
            );
          })()}
        </Box>
      </Box>
    </Box>
  );

  const showExportingBanner = (
    label: 'Current Request' | 'Historical Data'
  ) => {
    setBannerState({
      isVisible: true,
      title: l('Exporting'),
      description: `${l(label)} ${l(
        "export is running in background. You'll receive an email when your report is ready. You can also download it later from My Downloads in your profile."
      )}`,
      colorTheme: 'Information',
      autoDismissDelay: 8000,
      linkComponent: (
        <DSLink
          text='Go to My Downloads'
          color='neutral'
          url='/ECM/jbpmworkflowmanagement/downloadfiles'
          suffixIconKind='ChevronRight'
        />
      ),
    });
  };

  const triggerExport = (
    payload: {
      locale: string;
      connectionType?: string;
      filters: Record<string, unknown>;
    },
    label: 'Current Request' | 'Historical Data'
  ) => {
    createExport(payload)
      .unwrap()
      .then(() => {
        showExportingBanner(label);
      })
      .catch((e: unknown) => {
        const message = extractApiErrorMessage(
          e,
          l('Export failed. Please try again.')
        );

        setBannerState({
          isVisible: true,
          title: l('Export failed'),
          description: message,
          colorTheme: 'Critical',
          autoDismissDelay: 5000,
          linkComponent: null,
        });
      });
  };

  const handleHistoricalDataExport = () => {
    const logCreatedStartDate = toUtcIso(startDate);
    const logCreatedEndDate = toUtcIso(endDate);

    const payload: {
      locale: string;
      connectionType?: string;
      filters: Record<string, unknown>;
    } = {
      locale: appLocale,
      connectionType: connectionType ?? undefined,
      filters: {
        logCreatedStartDate,
        logCreatedEndDate,
      },
    };

    triggerExport(payload, 'Historical Data');
  };

  const handleCurrentRequestExport = () => {
    if (!requestAccessKey) {
      return;
    }

    const payload: {
      locale: string;
      connectionType?: string;
      filters: Record<string, unknown>;
    } = {
      locale: appLocale,
      connectionType: connectionType ?? undefined,
      filters: {
        requestAccessKeys: [requestAccessKey],
      },
    };

    triggerExport(payload, 'Current Request');
  };

  const handleAuditLogExport = () => {
    if (radioValue === 'detail') {
      handleHistoricalDataExport();

      return;
    }

    handleCurrentRequestExport();
  };

  const footer = (
    <FooterActionBar size='large'>
      <Box className='ReportModal-footer'>
        <Button kind='subtle' size='medium' onClick={onClose}>
          {l('Cancel')}
        </Button>
        <div className='ReportModal-footer--primary'>
          <Button
            kind='filled'
            size='medium'
            onClick={handleAuditLogExport}
            isDisabled={isLoading || isDetailInvalid}>
            {isLoading ? l('Exporting...') : l('Export')}
          </Button>
        </div>
      </Box>
    </FooterActionBar>
  );

  return (
    <ModalIsland
      onClose={onClose}
      isOpen={isOpen}
      HeaderComp={headerComp}
      contentBody={body}
      FooterActionBarComp={footer}
      className='ReportModal'
    />
  );
}

AuditLogReportModal.defaultProps = {
  requestAccessKey: undefined,
};

export default AuditLogReportModal;
